import type { Database } from '@planmax/shared-types'
import type { BadgeProps } from '@/components/ui/badge'
import type { SavedViewTabConfig, ColumnGroupConfig } from '@/components/data-table'
import type { RecordStatusFilter } from '@/types/record-status'
import {
  FUEL_PRICE_TYPES as _FUEL_PRICE_TYPES,
  FUEL_PRICE_TYPE_LABELS as _FUEL_PRICE_TYPE_LABELS,
} from '@/types/fuel-transactions'

// ============================================
// Enums from Database
// ============================================

export type LeaseType = Database['public']['Enums']['lease_type']
export type LeaseStatus = Database['public']['Enums']['lease_status']
export type EscalationType = Database['public']['Enums']['escalation_type']
export type RateType = Database['public']['Enums']['rate_type']
export type RenewalStatus = Database['public']['Enums']['renewal_status']
export type UnitType = Database['public']['Enums']['unit_type']
export type FuelType = Database['public']['Enums']['fuel_type']
export type FuelPriceType = Database['public']['Enums']['fuel_price_type']

// Re-export fuel price constants for use in lease forms
export const FUEL_PRICE_TYPES = _FUEL_PRICE_TYPES
export const FUEL_PRICE_TYPE_LABELS = _FUEL_PRICE_TYPE_LABELS

// ============================================
// Enum Arrays for Iteration
// ============================================

export const LEASE_TYPES = ['long_term', 'month_to_month', 'seasonal'] as const
export const LEASE_STATUSES = ['draft', 'pending_signature', 'active', 'expiring', 'expired', 'terminated', 'completed', 'cancelled'] as const
export const ESCALATION_TYPES = ['none', 'fixed_pct', 'cpi'] as const
export const RATE_TYPES = ['flat', 'per_sqft', 'per_night'] as const
export const RENEWAL_STATUSES = ['not_started', 'offer_sent', 'negotiating', 'signed', 'declined'] as const
export const FUEL_TYPES = ['jet_a', 'avgas'] as const

// ============================================
// Enum Labels
// ============================================

export const LEASE_TYPE_LABELS: Record<LeaseType, string> = {
  long_term: 'Long Term',
  month_to_month: 'Month to Month',
  seasonal: 'Seasonal',
}

export const LEASE_STATUS_LABELS: Record<LeaseStatus, string> = {
  draft: 'Draft',
  pending_signature: 'Pending Signature',
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  terminated: 'Terminated',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

// ============================================
// Status Transition Helpers
// ============================================

export function canTerminate(status: LeaseStatus): boolean {
  return ['active', 'expiring', 'expired'].includes(status)
}

export function canCancel(status: LeaseStatus): boolean {
  return ['draft', 'pending_signature'].includes(status)
}

export function canComplete(status: LeaseStatus): boolean {
  return ['active', 'expiring', 'expired', 'terminated'].includes(status)
}

export const ESCALATION_TYPE_LABELS: Record<EscalationType, string> = {
  none: 'None',
  fixed_pct: 'Fixed Percentage',
  cpi: 'CPI Adjusted',
}

export const RATE_TYPE_LABELS: Record<RateType, string> = {
  flat: 'Flat Rate',
  per_sqft: 'Per Sq Ft',
  per_night: 'Per Night',
}

export const RENEWAL_STATUS_LABELS: Record<RenewalStatus, string> = {
  not_started: 'Not Started',
  offer_sent: 'Offer Sent',
  negotiating: 'Negotiating',
  signed: 'Signed',
  declined: 'Declined',
}

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  jet_a: 'Jet-A',
  avgas: 'AvGas 100LL',
}

// ============================================
// Badge Variants for Status
// ============================================

type BadgeVariant = BadgeProps['variant']

export const LEASE_STATUS_VARIANTS: Record<LeaseStatus, BadgeVariant> = {
  draft: 'secondary',
  pending_signature: 'secondary',
  active: 'default',
  expiring: 'secondary',
  expired: 'destructive',
  terminated: 'destructive',
  completed: 'outline',
  cancelled: 'secondary',
}

export const LEASE_TYPE_VARIANTS: Record<LeaseType, BadgeVariant> = {
  long_term: 'default',
  month_to_month: 'secondary',
  seasonal: 'outline',
}

export const RENEWAL_STATUS_VARIANTS: Record<RenewalStatus, BadgeVariant> = {
  not_started: 'outline',
  offer_sent: 'secondary',
  negotiating: 'secondary',
  signed: 'default',
  declined: 'destructive',
}

// ============================================
// Table Row Type (with joined data)
// ============================================

export interface LeaseTableRow {
  // Core
  id: string
  organization_id: string
  location_id: string
  contract_number: string

  // Parties (with joined names)
  client_id: string
  client_name: string | null
  contact_id: string | null
  contact_name: string | null
  unit_id: string
  unit_code: string | null
  building_id: string | null
  building_name: string | null
  location_name: string | null

  // Unit type (joined from units.type)
  unit_type: UnitType | null

  // Term
  type: LeaseType
  start_date: string
  end_date: string | null
  term_months: number | null

  // Financials
  monthly_rate: number
  rate_basis: RateType | null
  security_deposit: number | null

  // Escalation
  escalation_type: EscalationType | null
  escalation_pct: number | null
  escalation_date: string | null

  // Renewal
  auto_renew: boolean
  renewal_notice_days: number | null
  renewal_status: RenewalStatus | null
  renewal_contacted_date: string | null

  // Transient options
  allow_transient_sublease: boolean
  transient_revenue_share_pct: number | null

  // Guarantees
  placement_guarantee: boolean
  requires_unblocked_access: boolean

  // Fuel
  fuel_type: FuelType | null
  fuel_commitment_gallons_monthly: number | null
  fuel_commitment_gallons_annual: number | null
  fuel_price_per_gallon: number | null
  fuel_price_type: FuelPriceType | null
  fuel_price_differential: number | null

  // Status
  status: LeaseStatus

  // Lifecycle
  termination_reason: string | null
  termination_date: string | null
  cancellation_reason: string | null

  // Documents
  document_url: string | null
  signed_at: string | null

  // Notes
  notes: string | null

  // Aircraft (from primary contract_aircraft join)
  primary_aircraft_tail: string | null
  primary_aircraft_type: string | null

  // Audit
  created_at: string
  updated_at: string | null
  deleted_at: string | null
}

// ============================================
// Form Data Type
// ============================================

export interface LeaseFormData {
  // Required fields
  location_id: string
  client_id: string
  unit_id: string
  type: LeaseType
  start_date: string
  monthly_rate: number
  status: LeaseStatus

  // Optional fields
  contact_id?: string | null
  end_date?: string | null
  term_months?: number | null
  rate_basis?: RateType | null
  security_deposit?: number | null

  // Escalation
  escalation_type?: EscalationType | null
  escalation_pct?: number | null
  escalation_date?: string | null

  // Renewal
  auto_renew?: boolean
  renewal_notice_days?: number | null

  // Transient options
  allow_transient_sublease?: boolean
  transient_revenue_share_pct?: number | null

  // Guarantees
  placement_guarantee?: boolean
  requires_unblocked_access?: boolean

  // Fuel
  fuel_type?: FuelType | null
  fuel_commitment_gallons_monthly?: number | null
  fuel_commitment_gallons_annual?: number | null
  fuel_price_per_gallon?: number | null
  fuel_price_type?: FuelPriceType | null
  fuel_price_differential?: number | null

  // Documents
  document_url?: string | null
  signed_at?: string | null

  // Notes
  notes?: string | null

  // Aircraft (junction table)
  aircraft_ids?: string[]
  primary_aircraft_id?: string | null
}

// ============================================
// Contract Aircraft (Junction Table)
// ============================================

export interface ContractAircraft {
  id: string
  lease_contract_id: string
  aircraft_id: string
  is_primary: boolean
  status: 'active' | 'inactive' | 'removed'
  added_at: string
  removed_at: string | null
  // Joined aircraft data
  tail_number?: string
  aircraft_type_name?: string | null
}

// ============================================
// Filter Type
// ============================================

export interface LeaseFilters {
  search?: string
  status?: LeaseStatus[]
  type?: LeaseType[]
  locationId?: string
  recordStatus?: RecordStatusFilter
}

// ============================================
// Legacy Page Tab Definitions (kept for reference)
// ============================================

export type LeaseStatusTab = 'active' | 'expiring' | 'draft' | 'expired' | 'all'

export interface LeaseStatusTabConfig {
  value: LeaseStatusTab
  label: string
  statuses: LeaseStatus[] | null // null = all statuses
  description?: string
}

export const LEASE_STATUS_TABS: LeaseStatusTabConfig[] = [
  { value: 'active', label: 'Active', statuses: ['active'] },
  { value: 'expiring', label: 'Expiring Soon', statuses: ['expiring'], description: '90 days or less' },
  { value: 'draft', label: 'Draft', statuses: ['draft', 'pending_signature'] },
  { value: 'expired', label: 'Expired', statuses: ['expired', 'terminated'] },
  { value: 'all', label: 'All', statuses: null },
]

/**
 * Get the tab config for a given tab value
 */
export function getLeaseTabConfig(tab: LeaseStatusTab): LeaseStatusTabConfig | undefined {
  return LEASE_STATUS_TABS.find((t) => t.value === tab)
}

// ============================================
// V2 SAVED VIEW TYPES
// ============================================

export type LeaseViewId = 'active_portfolio' | 'expiring' | 'drafts' | 'all'

export interface LeaseViewConfig extends SavedViewTabConfig<LeaseViewId> {
  defaultFilters: { status: LeaseStatus[] }
  defaultColumnGroups: LeaseColumnGroupId[]
}

export const LEASE_SAVED_VIEWS: LeaseViewConfig[] = [
  {
    id: 'active_portfolio',
    label: 'Active Portfolio',
    description: 'Active and expiring leases — your revenue base',
    defaultFilters: { status: ['active', 'expiring'] },
    defaultColumnGroups: ['identity', 'portfolio'],
  },
  {
    id: 'expiring',
    label: 'Expiring',
    description: 'Leases expiring within 90 days — renewal pipeline',
    defaultFilters: { status: ['active', 'expiring'] },
    defaultColumnGroups: ['identity', 'renewal'],
  },
  {
    id: 'drafts',
    label: 'Drafts',
    description: 'Draft and pending signature leases',
    defaultFilters: { status: ['draft', 'pending_signature'] },
    defaultColumnGroups: ['identity', 'drafts'],
  },
  {
    id: 'all',
    label: 'All',
    description: 'Every lease contract',
    defaultFilters: { status: [] },
    defaultColumnGroups: ['identity', 'general'],
  },
]

export function getLeaseViewConfig(viewId: LeaseViewId): LeaseViewConfig {
  return LEASE_SAVED_VIEWS.find((v) => v.id === viewId) ?? LEASE_SAVED_VIEWS[3]
}

// ============================================
// V2 COLUMN GROUPS
// ============================================

export type LeaseColumnGroupId = 'identity' | 'portfolio' | 'renewal' | 'drafts' | 'general' | 'financial'

export const LEASE_COLUMN_GROUPS: ColumnGroupConfig<LeaseColumnGroupId>[] = [
  { id: 'identity', label: 'Core', description: 'Contract#, Client, Unit, Aircraft, Term, Rate, Status', alwaysOn: true },
  { id: 'portfolio', label: 'Portfolio', description: 'Contract Type, Auto-Renew' },
  { id: 'renewal', label: 'Renewal', description: 'Days to Expiry, Renewal Status, Last Contact' },
  { id: 'drafts', label: 'Drafts', description: 'Created date, Days in Draft' },
  { id: 'general', label: 'General', description: 'Contract Type, Term type' },
  { id: 'financial', label: 'Financial', description: 'Security Deposit' },
]

// ============================================
// V2 GROUPING OPTIONS
// ============================================

export type LeaseGroupingOption = 'none' | 'building' | 'client' | 'contract_type' | 'renewal_status' | 'location'

export interface LeaseGroupingOptionConfig {
  value: LeaseGroupingOption
  label: string
}

export const LEASE_GROUPING_OPTIONS: LeaseGroupingOptionConfig[] = [
  { value: 'none', label: 'None' },
  { value: 'building', label: 'By Building / Unit' },
  { value: 'client', label: 'By Client' },
  { value: 'contract_type', label: 'By Contract Type' },
  { value: 'renewal_status', label: 'By Renewal Status' },
  { value: 'location', label: 'By Location' },
]

// ============================================
// V2 CONTRACT TYPE (derived from unit_type)
// ============================================

export type ContractType = 'hangar' | 'tiedown' | 'ramp' | 'other'

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  hangar: 'Hangar',
  tiedown: 'Tiedown',
  ramp: 'Ramp',
  other: 'Other',
}

export function getContractTypeFromUnit(unitType: UnitType | null): ContractType {
  if (!unitType) return 'other'
  if (['conventional_hangar', 'box_hangar', 't_hangar'].includes(unitType)) return 'hangar'
  if (unitType === 'tiedown') return 'tiedown'
  if (unitType === 'ramp') return 'ramp'
  return 'other'
}

// ============================================
// V2 STATUS FILTER PILL OPTIONS
// ============================================

export const LEASE_STATUS_FILTER_OPTIONS = LEASE_STATUSES.map((status) => ({
  value: status,
  label: LEASE_STATUS_LABELS[status],
}))

export const CONTRACT_TYPE_FILTER_OPTIONS: { value: ContractType; label: string }[] = [
  { value: 'hangar', label: 'Hangar' },
  { value: 'tiedown', label: 'Tiedown' },
  { value: 'ramp', label: 'Ramp' },
]

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate days until lease expiry
 */
export function getDaysUntilExpiry(endDate: string | null): number | null {
  if (!endDate) return null

  const end = new Date(endDate)
  const now = new Date()
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Calculate total monthly revenue at risk for expiring leases
 */
export function calculateRevenueAtRisk(leases: LeaseTableRow[]): {
  monthlyTotal: number
  yearlyTotal: number
  count: number
} {
  const expiringLeases = leases.filter((lease) => {
    const days = getDaysUntilExpiry(lease.end_date)
    return days !== null && days >= 0 && days <= 90
  })

  const monthlyTotal = expiringLeases.reduce((sum, lease) => sum + lease.monthly_rate, 0)

  return {
    monthlyTotal,
    yearlyTotal: monthlyTotal * 12,
    count: expiringLeases.length,
  }
}

/**
 * Format monthly rate as currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Calculate term months from start and end dates
 */
export function calculateTermMonths(startDate: string, endDate: string | null): number | null {
  if (!endDate) return null

  const start = new Date(startDate)
  const end = new Date(endDate)

  const months =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())

  return Math.max(0, months)
}

/**
 * Derive term type label from dates and type
 */
export function getTermTypeLabel(lease: Pick<LeaseTableRow, 'type' | 'start_date' | 'end_date'>): string {
  if (lease.type === 'month_to_month' || !lease.end_date) return 'M2M'
  const months = calculateTermMonths(lease.start_date, lease.end_date)
  if (months !== null && months > 12) return 'Multi-year'
  return 'Annual'
}

/**
 * Check if lease is expiring soon (within 30 days)
 */
export function isExpiringSoon(endDate: string | null): boolean {
  if (!endDate) return false

  const end = new Date(endDate)
  const now = new Date()
  const daysUntil = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  return daysUntil >= 0 && daysUntil <= 30
}

/**
 * Check if lease is expired
 */
export function isExpired(endDate: string | null): boolean {
  if (!endDate) return false

  const end = new Date(endDate)
  const now = new Date()

  return end < now
}

/**
 * Get display name for lease (contract number + client)
 */
export function getLeaseDisplayName(lease: Pick<LeaseTableRow, 'contract_number' | 'client_name'>): string {
  if (lease.client_name) {
    return `${lease.contract_number} - ${lease.client_name}`
  }
  return lease.contract_number
}

/**
 * Calculate days since lease was created (for draft status tracking)
 */
export function getDaysInDraft(createdAt: string): number {
  const created = new Date(createdAt)
  const now = new Date()
  return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
}
